import { useState, useEffect } from 'react';
import { Box, Typography, useTheme, CircularProgress } from '@mui/material';
import { tokens } from '../../theme';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import axios from 'axios';

const DecDailyStats = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [dailyData, setDailyData] = useState(null);

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCompact = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(2)}M`;
    }
    if (absValue >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  const fetchDailyStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.elmo-burns.com/api/v1/dec-distribution-history/');
      
      if (response.data && response.data.results && response.data.results.length >= 2) {
        const today = response.data.results[0];
        const yesterday = response.data.results[1];
        
        // Calculate daily changes
        const totalChange = parseFloat(today.total_supply) - parseFloat(yesterday.total_supply);
        const burnedChange = parseFloat(today.burned_amount) - parseFloat(yesterday.burned_amount);
        const daoChange = parseFloat(today.dao_balance) - parseFloat(yesterday.dao_balance);
        const landStakedChange = parseFloat(today.land_staked) - parseFloat(yesterday.land_staked);
        
        setDailyData({
          netProduction: totalChange,
          burnedChange: Math.abs(burnedChange),
          daoChange,
          landStakedChange
        });
      }
      
    } catch (err) {
      console.error('Failed to fetch daily stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyStats();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchDailyStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (!dailyData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="caption" color={colors.grey[400]}>
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Net Production */}
      <Box 
        sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: colors.primary[500],
          borderRadius: '8px',
          border: `1px solid ${dailyData.netProduction >= 0 ? colors.greenAccent[500] : colors.redAccent[500]}`
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color={colors.grey[300]}>
            Daily Net Change
          </Typography>
          {dailyData.netProduction >= 0 ? (
            <TrendingUpIcon sx={{ color: colors.greenAccent[400], fontSize: 20 }} />
          ) : (
            <TrendingDownIcon sx={{ color: colors.redAccent[400], fontSize: 20 }} />
          )}
        </Box>
        <Typography 
          variant="h4" 
          sx={{ 
            color: dailyData.netProduction >= 0 ? colors.greenAccent[400] : colors.redAccent[400],
            fontWeight: 'bold'
          }}
        >
          {dailyData.netProduction >= 0 ? '+' : ''}{formatCompact(dailyData.netProduction)}
        </Typography>
        <Typography variant="caption" color={colors.grey[400]}>
          {formatNumber(Math.abs(dailyData.netProduction))} DEC
        </Typography>
      </Box>

      {/* Burned Daily */}
      <Box 
        sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: colors.primary[500],
          borderRadius: '8px',
          border: `1px solid ${colors.redAccent[600]}`
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color={colors.grey[300]}>
            Daily Burned
          </Typography>
          <LocalFireDepartmentIcon sx={{ color: colors.redAccent[400], fontSize: 20 }} />
        </Box>
        <Typography variant="h4" sx={{ color: colors.redAccent[400], fontWeight: 'bold' }}>
          {formatCompact(dailyData.burnedChange)}
        </Typography>
        <Typography variant="caption" color={colors.grey[400]}>
          {formatNumber(dailyData.burnedChange)} DEC removed
        </Typography>
      </Box>

      {/* DAO Change */}
      <Box 
        sx={{ 
          mb: 2,
          p: 2, 
          backgroundColor: colors.primary[500],
          borderRadius: '8px',
          border: `1px solid rgba(232, 168, 56, 0.6)`
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color={colors.grey[300]}>
            DAO Change
          </Typography>
          {dailyData.daoChange >= 0 ? (
            <TrendingUpIcon sx={{ color: '#e8a838', fontSize: 20 }} />
          ) : (
            <TrendingDownIcon sx={{ color: '#e8a838', fontSize: 20 }} />
          )}
        </Box>
        <Typography variant="h4" sx={{ color: '#e8a838', fontWeight: 'bold' }}>
          {dailyData.daoChange >= 0 ? '+' : ''}{formatCompact(dailyData.daoChange)}
        </Typography>
        <Typography variant="caption" color={colors.grey[400]}>
          {formatNumber(Math.abs(dailyData.daoChange))} DEC
        </Typography>
      </Box>

    </Box>
  );
};

export default DecDailyStats;
